/**
 * One membership row of the upcoming-work index: every scheduleId whose Task is due (or explicitly
 * skipped) on a single local date AT ONE SITE, sharded within that site by
 * `scheduleId mod PlannedTaskDueDayBucketCount` so a Task only ever touches one document per date.
 * Materialised calendar dues only — daily/weekday classes are evaluated from the Task, and
 * everyVisit/nthVisit never appear here.
 *
 * WBS-11102 — the key is `(date, siteId, bucket)` and site LEADS it. Every board view is site-ordered
 * or site-filtered, so a site-leading key lets a read sort and page on the index itself, before any
 * `$lookup` hydrates a task. Keyed on `(date, bucket)` alone the read had to join first to find out
 * which site a row belonged to, which is what hydrated 115k rows to serve one page.
 *
 * A PURE PLAN PROJECTION (task-first movement, Stage 4): only the projector writes here, and nothing a
 * Visit does is recorded on this row. Moves, cancellations and added work live on the stored Visit task
 * rows (`planned_visit_tasks`), which every reader overlays on this membership at read time.
 */
export interface PlannedTaskDueDay {
    _id: string;
    /** YYYY-MM-DD in the Service Line timezone. */
    date: string;
    /** The site these dues belong to — the plan's `site._id`, or `DUE_DAY_NO_SITE` when it has none. */
    siteId: string;
    /** The scheduleId shard this row covers WITHIN its site: `scheduleId % PlannedTaskDueDayBucketCount`. */
    bucket: number;
    /** scheduleIds due on this date. */
    include: number[];
    /** scheduleIds that would otherwise be due on this date but are skipped (EXDATE / blackout). */
    exclude: number[];
    /**
     * WBS-10984 — which schedule revision put each id in `include` / `exclude` on this date.
     *
     * Provenance rides in a SIDECAR array rather than inside `include` / `exclude`, which stay plain
     * `number[]`. That is not stylistic: `{ include: 1, date: 1 }` and `{ exclude: 1, date: 1 }` are
     * multikey indexes over scalars, the Service Line board `$lookup`s straight onto `include`, and
     * every read path iterates raw ids. Turning those elements into objects would break all three at
     * once, and for a migration this fix does not need.
     *
     * Only the projection's own membership writes stamp it, so a row can legitimately carry fewer
     * `writes` than ids: anything written before WBS-10984, or by the completion re-point, has no
     * entry here and is therefore out of reach of the revision-scoped withdrawal below.
     */
    writes?: PlannedTaskDueDayWrite[];
}

/** One membership write's provenance: who wrote it, under which revision, into which array. */
export interface PlannedTaskDueDayWrite {
    scheduleId: number;
    revision: number;
    kind: 'include' | 'exclude';
}

/**
 * The one fixed collection holding the upcoming-work index. Tenant DB only.
 *
 * The collection name is persisted storage: pointing the model at a new name without moving its rows
 * would present an existing tenant with an empty board.
 */
export const PlannedTaskDueDayCollectionName = 'planned_task_due_days';

/**
 * Where a due lands when its plan has no site at all. A real site id can never collide with it.
 *
 * The key cannot be sparse: `{ date, siteId, bucket }` is unique, and a missing `siteId` would make
 * every siteless row on a date collide on `null` in some drivers and not in others. A single explicit
 * sentinel keeps siteless dues in exactly one predictable place, so the same site-leading read walks
 * them like any other site instead of needing a second, unindexed branch.
 */
export const DUE_DAY_NO_SITE = '__nosite__';

/**
 * How many buckets share one site's due-day index for one date. Fixed at compile time, not a range:
 * every scheduleId hashes into `[0, PlannedTaskDueDayBucketCount)` by modulo, so bucket count
 * never grows as the sequencer climbs.
 *
 * SITE leads the key; this only caps ONE enormous site. A site is already a natural shard — dues
 * spread across sites without any hashing — so the bucket exists purely so the biggest single site
 * cannot grow one unbounded array: a ~3,000-task site splits into arrays of ~375 rather than one of
 * 3,000.
 *
 * N is deliberately SMALL. At 64 an ordinary 5–10-task site scatters into five to ten documents
 * holding one id each, and the read then pays a document per due — the opposite of what the index is
 * for. 8 keeps a typical site in one or two rows while still capping the outlier.
 *
 * This is a compile-time constant, deliberately not a tenant setting. Changing it would strand every
 * existing row — ids would hash to a bucket other than the one holding them, and nothing in the read
 * path would notice — so it is frozen once data exists.
 */
export const PlannedTaskDueDayBucketCount = 8;

/** Bucket a scheduleId by modulo into one of the fixed buckets. Dense uint32 ids, so this never grows. */
export function dueDayBucket(scheduleId: number): number {

    if (!Number.isInteger(scheduleId) || scheduleId < 0) throw new Error(`scheduleId must be a non-negative integer, received ${scheduleId}`);
    return scheduleId % PlannedTaskDueDayBucketCount;
}

/** One (date, siteId, bucket) document's worth of scheduleIds, ready for a single `$addToSet: { $each }` upsert. */
export interface PlannedTaskDueDayEntry {
    date: string;
    siteId: string;
    bucket: number;
    scheduleIds: number[];
    /**
     * The schedule revision this entry was exploded for. Present, the repository records provenance
     * alongside the membership so the run can withdraw exactly these ids if it later loses the fence;
     * absent, the write is unstamped and behaves exactly as it did before WBS-10984.
     */
    revision?: number;
}

/**
 * Collapse `(scheduleId, date, siteId)` occurrences into one entry per `(date, siteId, bucket)` so a
 * horizon expansion writes one document per (date, site, range) instead of one update per (task, date).
 * Duplicate ids within a bucket are dropped here; `$addToSet` makes a replayed tick harmless either way.
 */
export function groupDueDayEntries(occurrences: { scheduleId: number; date: string; siteId: string }[], revision?: number): PlannedTaskDueDayEntry[] {

    const grouped = new Map<string, { date: string; siteId: string; bucket: number; scheduleIds: Set<number> }>();
    for (const occurrence of occurrences) {
        const bucket = dueDayBucket(occurrence.scheduleId);
        const siteId = occurrence.siteId || DUE_DAY_NO_SITE;
        const key = `${occurrence.date}|${siteId}|${bucket}`;
        const entry = grouped.get(key) ?? { date: occurrence.date, siteId, bucket, scheduleIds: new Set<number>() };
        entry.scheduleIds.add(occurrence.scheduleId);
        grouped.set(key, entry);
    }
    return [...grouped.values()].map(entry => revision === undefined
        ? { date: entry.date, siteId: entry.siteId, bucket: entry.bucket, scheduleIds: [...entry.scheduleIds] }
        : { date: entry.date, siteId: entry.siteId, bucket: entry.bucket, scheduleIds: [...entry.scheduleIds], revision });
}
